import { useMemo } from "react";

import { YESTERDAY_DATE_RANGE, EOM_DATE_STR } from "app/utils/date";
import { percentChanged } from "app/utils/math";

import { getCostFieldName } from "./filterTransformUtils";
import { useCostReportingConfig } from "./useCostReportingConfig";
import { useCostSavingOpportunity } from "./useCostSavingOpportunity";
import { useCostSavingOpportunityTag } from "./useCostSavingOpportunityTag";

import type { CostSavingOpportunityResult } from "./useCostSavingOpportunity.schema";
import type { CostSavingOpportunityTagResult } from "./useCostSavingOpportunityTag.schema";
import type { CloudCostType } from "app/contexts/UserContext";
import type { FilterBarCubeFilters } from "app/views/dashboards/utils/costDashboardFilterBar";

export interface SavingsNonCompliantResourceResult {
  value?: number | null;
  previous?: number | null;
  diff: number | null;
}

export type UseMonthlySavingResult = [
  monthlySavingOpportunity: SavingsNonCompliantResourceResult,
  nonCompliantResourceCount: SavingsNonCompliantResourceResult,
  isLoading: boolean,
];

/**
 * Helper function to extract the appropriate cost field value from a data item
 *
 * Uses getCostFieldName to determine the field name, then extracts the value from the item
 */
function getCostField(
  item: CostSavingOpportunityResult | CostSavingOpportunityTagResult,
  costType: CloudCostType | undefined,
  isByTag: boolean,
): string | undefined | null {
  const prefix = isByTag ? "CSOTag" : "CSO";
  const fieldName = getCostFieldName(costType, prefix);
  return item[fieldName as keyof typeof item];
}

/**
 * Helper function to aggregate cost saving opportunity data
 */
function aggregateData(
  data: CostSavingOpportunityResult[] | CostSavingOpportunityTagResult[] | null,
  costType: CloudCostType | undefined,
  isByTag: boolean,
): { savingOpportunity: number | null; resourceCount: number | null } {
  if (!data || data.length === 0) {
    return { savingOpportunity: null, resourceCount: null };
  }

  const savingOpportunity = data.reduce((sum, item) => {
    const value = getCostField(item, costType, isByTag);
    return sum + (Number(value) || 0);
  }, 0);

  const prefix = isByTag ? "CSOTag" : "CSO";
  const resourceCount = data.reduce((sum, item) => {
    const value =
      item[`${prefix}.nonCompliantResourceCount` as keyof typeof item];
    return sum + (Number(value) || 0);
  }, 0);

  return { savingOpportunity, resourceCount };
}

/**
 * Hook to switch between fetching monthly savings opp and non-compliant resource count with or without resource tag filtering
 * @param dashboardKey - dashboard key
 * @param isResourceFiltering - if there are resource filters
 * @param tag - optional resource tag key for filtering, used whole as tag keys can contain `:`
 * @param filters - the query filters
 * @param sectionKey - dashboard section key
 * @returns monthly savings opp, non-compliant resource count and loading state
 */
export function useMonthlySavingOpportunitySelector(
  dashboardKey: string,
  isResourceFiltering: boolean,
  tag?: string,
  filters?: FilterBarCubeFilters,
  sectionKey?: string,
): UseMonthlySavingResult {
  const costType = useCostReportingConfig();

  // Query without tag filtering
  const { data: currDataNoTag, isLoading: isLoadingCurrNoTag } =
    useCostSavingOpportunity({
      dashboardKey,
      dateRange: YESTERDAY_DATE_RANGE as "Yesterday",
      sectionKey,
      filters,
      dashboardSummary: !sectionKey,
      enabled: !isResourceFiltering,
    });

  const { data: prevDataNoTag, isLoading: isLoadingPrevNoTag } =
    useCostSavingOpportunity({
      dashboardKey,
      dateRange: [EOM_DATE_STR, EOM_DATE_STR] as [string, string],
      sectionKey,
      filters,
      dashboardSummary: !sectionKey,
      enabled: !isResourceFiltering,
    });

  // Query with tag filtering
  const { data: currDataByTag, isLoading: isLoadingCurrByTag } =
    useCostSavingOpportunityTag({
      dashboardKey,
      dateRange: YESTERDAY_DATE_RANGE as "Yesterday",
      sectionKey,
      resourceTagKey: tag,
      filters,
      dashboardSummary: !sectionKey,
      enabled: isResourceFiltering,
    });

  const { data: prevDataByTag, isLoading: isLoadingPrevByTag } =
    useCostSavingOpportunityTag({
      dashboardKey,
      dateRange: [EOM_DATE_STR, EOM_DATE_STR] as [string, string],
      sectionKey,
      resourceTagKey: tag,
      filters,
      dashboardSummary: !sectionKey,
      enabled: isResourceFiltering,
    });

  const result = useMemo(() => {
    const currData = isResourceFiltering ? currDataByTag : currDataNoTag;
    const prevData = isResourceFiltering ? prevDataByTag : prevDataNoTag;

    const {
      savingOpportunity: currSavingOpportunity,
      resourceCount: currResourceCount,
    } = aggregateData(currData, costType, isResourceFiltering);
    const {
      savingOpportunity: prevSavingOpportunity,
      resourceCount: prevResourceCount,
    } = aggregateData(prevData, costType, isResourceFiltering);

    const savingOpportunityDiff = percentChanged(
      currSavingOpportunity || 0,
      prevSavingOpportunity || 0,
    );

    const resourceCountDiff = percentChanged(
      currResourceCount || 0,
      prevResourceCount || 0,
    );

    const monthlySavingOpportunity: SavingsNonCompliantResourceResult = {
      value: currSavingOpportunity,
      previous: prevSavingOpportunity,
      diff: savingOpportunityDiff,
    };

    const nonCompliantResourceCount: SavingsNonCompliantResourceResult = {
      value: currResourceCount,
      previous: prevResourceCount,
      diff: resourceCountDiff,
    };

    return { monthlySavingOpportunity, nonCompliantResourceCount };
  }, [
    currDataNoTag,
    prevDataNoTag,
    currDataByTag,
    prevDataByTag,
    costType,
    isResourceFiltering,
  ]);

  const isLoading = isResourceFiltering
    ? isLoadingCurrByTag || isLoadingPrevByTag
    : isLoadingCurrNoTag || isLoadingPrevNoTag;

  return [
    result.monthlySavingOpportunity,
    result.nonCompliantResourceCount,
    isLoading,
  ];
}
